import { useCallback, useEffect, useRef, useState } from 'react';
import { createPortal } from 'react-dom';
import { Box, Row, Typography } from '@tempmee/react';
import { RatingContent } from '~/components/ProfessionalsHub/components/ProfessionalRating/RatingContent';
import { SolidStarIcon } from '@tempmee/react-icons';
import { colors } from '@tempmee/tailwind';

interface IEmbeddedRatingPopoverProps {
  averageRating: number;
  professionalSkills?: number;
  interpersonalSkills?: number;
  timeManagement?: number;
}

const POPOVER_HEIGHT_BUFFER = 280;
const POPOVER_OFFSET = 12;
const POPOVER_WIDTH = 220;

export const EmbeddedRatingPopover = ({
  averageRating,
  professionalSkills,
  interpersonalSkills,
  timeManagement,
}: IEmbeddedRatingPopoverProps) => {
  const triggerRef = useRef<HTMLDivElement | null>(null);
  const [isOpen, setIsOpen] = useState(false);
  const [coords, setCoords] = useState<{ top: number; left: number; placement: 'top' | 'bottom' }>(
    { top: 0, left: 0, placement: 'bottom' },
  );
  const closeTimerRef = useRef<ReturnType<typeof setTimeout> | null>(null);

  const hasBreakdown =
    professionalSkills != null || interpersonalSkills != null || timeManagement != null;
  const useHoverTrigger = averageRating === 5;

  const updateCoords = useCallback(() => {
    if (!triggerRef.current) return;
    const rect = triggerRef.current.getBoundingClientRect();
    const viewportHeight = window.innerHeight;
    const spaceBelow = viewportHeight - rect.bottom;
    const placement: 'top' | 'bottom' =
      spaceBelow < POPOVER_HEIGHT_BUFFER ? 'top' : 'bottom';
    const left = rect.left + rect.width / 2 - POPOVER_WIDTH / 2;
    const top =
      placement === 'bottom'
        ? rect.bottom + POPOVER_OFFSET
        : rect.top - POPOVER_OFFSET;
    setCoords({ top, left, placement });
  }, []);

  useEffect(() => {
    if (!isOpen) return undefined;
    updateCoords();
    window.addEventListener('resize', updateCoords);
    window.addEventListener('scroll', updateCoords, true);
    return () => {
      window.removeEventListener('resize', updateCoords);
      window.removeEventListener('scroll', updateCoords, true);
    };
  }, [isOpen, updateCoords]);

  useEffect(() => {
    if (!isOpen || useHoverTrigger) return undefined;
    const handleClickOutside = (event: MouseEvent) => {
      if (
        triggerRef.current &&
        !triggerRef.current.contains(event.target as Node)
      ) {
        setIsOpen(false);
      }
    };
    document.addEventListener('mousedown', handleClickOutside);
    return () => document.removeEventListener('mousedown', handleClickOutside);
  }, [isOpen, useHoverTrigger]);

  useEffect(() => {
    return () => {
      if (closeTimerRef.current) clearTimeout(closeTimerRef.current);
    };
  }, []);

  const handleEnter = () => {
    if (!useHoverTrigger) return;
    if (closeTimerRef.current) {
      clearTimeout(closeTimerRef.current);
      closeTimerRef.current = null;
    }
    setIsOpen(true);
  };

  const handleLeave = () => {
    if (!useHoverTrigger) return;
    closeTimerRef.current = setTimeout(() => setIsOpen(false), 120);
  };

  const handleClick = (e: React.MouseEvent) => {
    if (useHoverTrigger) return;
    e.stopPropagation();
    setIsOpen((prev) => !prev);
  };

  if (!hasBreakdown) {
    return (
      <Row align="center" gap={1} data-testid="rating-static">
        <SolidStarIcon size={24} color={colors.success[400]} />
        <Typography variant="subtitle1" color={colors.neutral[600]}>
          {averageRating.toFixed(1)}
        </Typography>
      </Row>
    );
  }

  const popover = isOpen
    ? createPortal(
      <Box
          onMouseEnter={handleEnter}
          onMouseLeave={handleLeave}
          onClick={(e) => e.stopPropagation()}
          style={{
            position: 'fixed',
            top: coords.placement === 'bottom' ? coords.top : undefined,
            bottom:
              coords.placement === 'top'
                ? window.innerHeight - coords.top
                : undefined,
            left: coords.left,
            width: POPOVER_WIDTH,
            backgroundColor: '#fff',
            borderRadius: 12,
            padding: 16,
            zIndex: 1000,
            boxShadow: '0px 6px 10px rgba(0,0,0,0.15), 0px 2px 3px rgba(0,0,0,0.15)',
          }}
        >
        <Box
            aria-hidden
            style={{
              position: 'absolute',
              left: '50%',
              width: 14,
              height: 14,
              backgroundColor: '#fff',
              transform: 'translateX(-50%) rotate(45deg)',
              boxShadow: '-2px -2px 3px rgba(0,0,0,0.08)',
              ...(coords.placement === 'bottom' ? { top: -7 } : { bottom: -7 }),
            }}
          />
        <Box style={{ position: 'relative', backgroundColor: '#fff', borderRadius: 12 }}>
          <RatingContent
              professionalSkills={professionalSkills ?? 5}
              interpersonalSkills={interpersonalSkills ?? 5}
              timeManagement={timeManagement ?? 5}
            />
        </Box>
      </Box>,
        document.body,
      )
    : null;

  return (
    <Row
      align="center"
      gap={1}
      data-testid={useHoverTrigger ? 'rating-hover' : 'rating-click'}
    >
      <SolidStarIcon size={24} color={colors.success[400]} />
      <Box
        as="span"
        ref={triggerRef as React.Ref<HTMLDivElement>}
        onMouseEnter={handleEnter}
        onMouseLeave={handleLeave}
        onClick={handleClick}
        style={{
          textDecoration: 'underline',
          color: colors.neutral[600],
          cursor: useHoverTrigger ? 'default' : 'pointer',
        }}
      >
        <Typography variant="subtitle1" as="span">
          {averageRating.toFixed(1)}
        </Typography>
      </Box>
      {popover}
    </Row>
  );
};
